import { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Calendar, MessageSquare } from 'lucide-react';
import api from '@/lib/api';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Settings() {
  const [loading, setLoading] = useState(false);
  const [calendarConnected, setCalendarConnected] = useState(false);

  useEffect(() => {
    checkCalendarConnection();
  }, []);

 const checkCalendarConnection = async () => {
  try {
    const token = localStorage.getItem("slotrescue_token");

    const res = await axios.get(
      `${API_URL}/calendar/events`,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    // if response succeeds -> calendar connected
    setCalendarConnected(true);

  } catch (error) {
    setCalendarConnected(false);
  }
};



  const handleConnectCalendar = async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/google/login`);
      window.location.href = response.data.authorization_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to connect calendar');
    }
  };

  return (
    <Layout active="settings">
      <div data-testid="settings-page" className="space-y-8">
        <div>
          <h1 className="text-4xl font-bold text-gray-900" style={{ fontFamily: 'Manrope' }}>
            Settings
          </h1>
          <p className="text-gray-600 mt-2">Configure your SlotRescue integrations</p>
        </div>

        <div className="space-y-6">
          <Card className="bg-white border-border shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Google Calendar Integration
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-600">
                Connect your Google Calendar to automatically detect cancelled appointments.
              </p>
              {calendarConnected ? (
                <div className="flex items-center gap-2 text-green-600">
                  <div className="h-2 w-2 bg-green-600 rounded-full"></div>
                  <span className="text-sm font-medium">Connected</span>
                </div>
              ) : (
                <Button onClick={handleConnectCalendar} data-testid="connect-calendar-button">
                  Connect Google Calendar
                </Button>
              )}
            </CardContent>
          </Card>

          <Card className="bg-white border-border shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                Twilio SMS Configuration
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-600">
                Configure Twilio credentials in your backend .env file:
              </p>
              <div className="space-y-2 bg-gray-50 p-4 rounded-md text-sm font-mono">
                <p>TWILIO_ACCOUNT_SID=your_account_sid</p>
                <p>TWILIO_AUTH_TOKEN=your_auth_token</p>
                <p>TWILIO_PHONE_NUMBER=your_phone_number</p>
              </div>
              <p className="text-xs text-gray-500">
                Get your credentials from{' '}
                <a
                  href="https://www.twilio.com/console"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-teal-600 hover:underline"
                >
                  Twilio Console
                </a>
              </p>
            </CardContent>
          </Card>

          <Card className="bg-white border-border shadow-sm">
            <CardHeader>
              <CardTitle>Notification Rules</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-600">
                Configure how customers are notified about available slots.
              </p>
              <div className="space-y-3">
                <div>
                  <Label>Initial notification count</Label>
                  <Input type="number" defaultValue="2" className="mt-1" disabled />
                  <p className="text-xs text-gray-500 mt-1">
                    Number of customers to notify first (currently: top 2)
                  </p>
                </div>
                <div>
                  <Label>Wait time before next batch</Label>
                  <Input type="number" defaultValue="5" className="mt-1" disabled />
                  <p className="text-xs text-gray-500 mt-1">
                    Minutes to wait before notifying next group
                  </p>
                </div>
              </div>
              <p className="text-xs text-gray-400">
                Advanced notification rules coming in future updates
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
